import { useTranslation } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import {
  Aperture,
  Timer,
  FilmStrip,
  Warning,
  FloppyDisk,
  Info,
  ShieldCheck,
  Lightning,
  Scales,
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.08 } },
};
const item = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35 } },
};

function SettingCard({ label, value, icon, testId, badge }) {
  return (
    <motion.div
      variants={item}
      className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-5 text-center hover:border-white/20 transition-colors"
    >
      <div className="flex items-center justify-center gap-1.5 mb-2">
        {icon}
        <span className="text-[#71717A] text-xs uppercase tracking-[0.15em] font-semibold">
          {label}
        </span>
      </div>
      <div
        className="font-['JetBrains_Mono'] text-3xl sm:text-4xl font-medium text-white tracking-tighter setting-value-glow"
        data-testid={testId}
      >
        {value}
      </div>
      {badge && <div className="mt-2">{badge}</div>}
    </motion.div>
  );
}

function buildExplanation(results, formData, t) {
  const parts = [];
  const isMacroLens = results.lens_type === "macro";

  if (isMacroLens) {
    parts.push(results.use_tripod ? t("exp_macro_lens_tripod") : t("exp_macro_lens"));
  } else {
    parts.push(t(`exp_${results.photo_type}`) || "");
    parts.push(results.use_tripod ? t("exp_tripod") : t("exp_handheld"));
  }

  const zone = results.iso_zone;
  if (zone === "caution") {
    parts.push(t("exp_iso_high"));
  } else if (zone === "acceptable") {
    parts.push(t("exp_iso_acceptable"));
  } else {
    parts.push(t("exp_iso_preferred"));
  }
  return parts.filter(Boolean).join(" ");
}

const ALT_CONFIG = {
  balanced: { Icon: Scales, color: "text-blue-400" },
  safer: { Icon: ShieldCheck, color: "text-emerald-400" },
  action: { Icon: Lightning, color: "text-[#F5A524]" },
};

function AlternativeCard({ alt, t }) {
  const cfg = ALT_CONFIG[alt.name] || ALT_CONFIG.balanced;
  const { Icon, color } = cfg;
  return (
    <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-5" data-testid={`alt-card-${alt.name}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon weight="fill" className={`w-4 h-4 ${color}`} />
        <span className="text-white font-medium text-sm">
          {t(`res_${alt.name}`)}
        </span>
        <span className="text-[#71717A] text-xs">
          {t(`res_${alt.name}_desc`)}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="text-center">
          <div className="text-[#71717A] text-xs mb-1">{t("res_aperture")}</div>
          <div className="font-['JetBrains_Mono'] text-lg text-white" data-testid={`alt-${alt.name}-aperture`}>
            {alt.aperture}
          </div>
        </div>
        <div className="text-center">
          <div className="text-[#71717A] text-xs mb-1">{t("res_shutter")}</div>
          <div className="font-['JetBrains_Mono'] text-lg text-white" data-testid={`alt-${alt.name}-shutter`}>
            {alt.shutter_speed}
          </div>
        </div>
        <div className="text-center">
          <div className="text-[#71717A] text-xs mb-1">{t("res_iso")}</div>
          <div className="font-['JetBrains_Mono'] text-lg text-white" data-testid={`alt-${alt.name}-iso`}>
            {alt.iso}
          </div>
        </div>
      </div>
    </div>
  );
}

function IsoZoneBadge({ zone, t }) {
  if (!zone) return null;
  return (
    <span
      className={cn(
        "text-xs px-2 py-0.5 rounded-md font-medium",
        zone === "ideal" && "bg-emerald-500/10 text-emerald-400",
        zone === "acceptable" && "bg-[#F5A524]/10 text-[#F5A524]",
        zone === "caution" && "bg-red-500/10 text-red-400"
      )}
      data-testid="iso-zone-badge"
    >
      {t(`res_iso_zone_${zone}`)}
    </span>
  );
}

export default function ResultsDisplay({
  results,
  onSave,
  formData,
  isAuthenticated,
}) {
  const { t } = useTranslation();

  if (!results) {
    return (
      <div className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-12 flex flex-col items-center justify-center min-h-[400px] text-center">
        <Aperture weight="thin" className="w-16 h-16 text-[#242429] mb-4" />
        <h3
          className="font-['Outfit'] text-xl text-white mb-2"
          data-testid="results-empty-title"
        >
          {t("res_empty_title")}
        </h3>
        <p className="text-[#71717A] max-w-sm">{t("res_empty")}</p>
      </div>
    );
  }

  const rec = results.recommended;
  const explanation = buildExplanation(results, formData, t);

  const isoDisplay =
    rec.iso_range && rec.iso_range[0] !== rec.iso_range[1]
      ? `${rec.iso_range[0]}-${rec.iso_range[1]}`
      : rec.iso;

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={JSON.stringify(rec)}
        variants={container}
        initial="hidden"
        animate="show"
        className="space-y-4"
        data-testid="results-display"
      >
        {/* EV Badge + Title */}
        <motion.div variants={item} className="flex items-center justify-between">
          <h2 className="font-['Outfit'] text-xl font-semibold text-white">
            {t("res_title")}
          </h2>
          <div className="flex items-center gap-2">
            <span className="text-[#71717A] text-xs uppercase tracking-wider">
              {t("res_ev")}
            </span>
            <span className="font-['JetBrains_Mono'] text-sm text-[#F5A524] bg-[#F5A524]/10 px-2 py-0.5 rounded-md" data-testid="ev-value">
              {results.effective_ev}
            </span>
          </div>
        </motion.div>

        {/* Main Settings Cards */}
        <div className="grid grid-cols-3 gap-3">
          <SettingCard
            label={t("res_aperture")}
            value={rec.aperture}
            icon={<Aperture weight="regular" className="w-3.5 h-3.5 text-[#F5A524]" />}
            testId="aperture-result"
          />
          <SettingCard
            label={t("res_shutter")}
            value={rec.shutter_speed}
            icon={<Timer weight="regular" className="w-3.5 h-3.5 text-[#F5A524]" />}
            testId="shutter-result"
          />
          <SettingCard
            label={t("res_iso")}
            value={isoDisplay}
            icon={<FilmStrip weight="regular" className="w-3.5 h-3.5 text-[#F5A524]" />}
            testId="iso-result"
            badge={<IsoZoneBadge zone={results.iso_zone} t={t} />}
          />
        </div>

        {/* Explanation */}
        <motion.div
          variants={item}
          className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-5"
        >
          <div className="flex items-center gap-2 mb-2">
            <Info weight="fill" className="w-4 h-4 text-[#F5A524]" />
            <h4 className="text-white font-medium text-sm">{t("res_explanation")}</h4>
          </div>
          <p className="text-[#A1A1AA] text-sm leading-relaxed" data-testid="explanation-text">
            {explanation}
          </p>
        </motion.div>

        {/* Warnings */}
        {results.warnings && results.warnings.length > 0 && (
          <motion.div
            variants={item}
            className="bg-[#121214]/80 backdrop-blur-md border border-[#F5A524]/30 rounded-xl p-5"
          >
            <div className="flex items-center gap-2 mb-3">
              <Warning weight="fill" className="w-4 h-4 text-[#F5A524]" />
              <h4 className="text-[#F5A524] font-medium text-sm">
                {t("res_warnings")}
              </h4>
            </div>
            <div className="space-y-2">
              {results.warnings.map((w) => (
                <p
                  key={w}
                  className="text-[#A1A1AA] text-sm flex items-start gap-2"
                  data-testid={`warning-${w}`}
                >
                  <span className="text-[#F5A524] mt-0.5 shrink-0">-</span>
                  {t(`warn_${w}`)}
                </p>
              ))}
            </div>
          </motion.div>
        )}

        {/* Camera Advice */}
        <motion.div
          variants={item}
          className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-5"
        >
          <h4 className="text-white font-medium text-sm mb-2">
            {t("res_camera_advice")}
          </h4>
          <p className="text-[#A1A1AA] text-sm leading-relaxed" data-testid="camera-advice-text">
            {t(`adv_${results.camera_advice_key}`)}
          </p>
        </motion.div>

        {/* Alternatives */}
        {results.alternatives && results.alternatives.length > 0 && (
          <motion.div variants={item}>
            <h4 className="text-white font-medium text-sm mb-3">
              {t("res_alternatives")}
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {results.alternatives.map((alt) => (
                <AlternativeCard key={alt.name} alt={alt} t={t} />
              ))}
            </div>
          </motion.div>
        )}

        {/* Save Button */}
        <motion.div variants={item}>
          <Button
            onClick={onSave}
            variant="outline"
            className="w-full border-white/10 text-white hover:bg-white/5 hover:border-white/20"
            data-testid="save-settings-button"
          >
            <FloppyDisk weight="regular" className="w-4 h-4 mr-2" />
            {t("res_save")}
            {!isAuthenticated && (
              <span className="text-[#71717A] text-xs ml-2">
                ({t("nav_login")})
              </span>
            )}
          </Button>
          <p className="text-[#52525B] text-xs text-center mt-3" data-testid="starting-note">
            {t("res_starting_note")}
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
